import {
  EliteLandingPageState,
  EliteOnboardingRenewalStatus,
  EliteSubscriptionAutoPayStatus,
  type EliteOnboardingStatusResponse,
  type EliteSubscriptionDetails
} from '../../../types/misc.types'

/** Epoch when the current subscription ends (supports alternate API keys). */
export function getEliteSubscriptionExpiryEpoch(details?: EliteSubscriptionDetails | null): number | undefined {
  if (!details) {
    return undefined
  }
  const a = details.subscriptionExpiredOn
  if (a != null && a !== 0) {
    return a
  }
  const b = details.subscriptionExpiresOn
  if (b != null && b !== 0) {
    return b
  }
  const c = details.subscriptionEndTime
  if (c != null && c !== 0) {
    return c
  }
  return undefined
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

function getLocalDayStartMs(epochMs: number): number {
  const date = new Date(epochMs)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/** Calendar days from today (local) until the expiry date; 0 = expires today, negative = past that day. */
export function getDaysUntilSubscriptionExpiry(expiryEpochMs?: number): number | null {
  if (expiryEpochMs == null || expiryEpochMs === 0) {
    return null
  }
  const expiryDayStart = getLocalDayStartMs(expiryEpochMs)
  const todayStart = getLocalDayStartMs(Date.now())
  return Math.round((expiryDayStart - todayStart) / MS_PER_DAY)
}

/** Advisory includes `renewal` when the renewal prompt and renewal CTA should be shown. */
export function shouldShowEliteRenewalPrompt(eliteOnboardingStatus?: EliteOnboardingStatusResponse | null): boolean {
  return Boolean(eliteOnboardingStatus?.renewal || eliteOnboardingStatus?.renewal?.cta)
}

export function isEliteAutoPayActive(eliteOnboardingStatus?: EliteOnboardingStatusResponse | null): boolean {
  return eliteOnboardingStatus?.subscriptionDetails?.autoPayStatus === EliteSubscriptionAutoPayStatus.ACTIVE
}

export function getEliteSubscriptionValidityDateLabel(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null
): 'Renews on' | 'Valid till' {
  return isEliteAutoPayActive(eliteOnboardingStatus) ? 'Renews on' : 'Valid till'
}

export function shouldHideEliteRenewalPlansForAutoPayRetrying(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null
): boolean {
  return eliteOnboardingStatus?.renewal?.status === EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING
}

/** True when the subscription expiry calendar day is before today, or landing state is plan expired. */
export function isEliteSubscriptionPastExpiry(eliteOnboardingStatus?: EliteOnboardingStatusResponse | null): boolean {
  if (isEliteAutoPayActive(eliteOnboardingStatus)) {
    return false
  }
  if (eliteOnboardingStatus?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED) {
    return true
  }
  const daysLeft = getDaysUntilSubscriptionExpiry(
    getEliteSubscriptionExpiryEpoch(eliteOnboardingStatus?.subscriptionDetails)
  )
  return daysLeft !== null && daysLeft < 0
}

/** Grace / locked banners replace renew headlines; renew copy only when lock status is `NA`. */
export function shouldShowElitePortfolioInsightsLockBanner(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null
): boolean {
  const status = eliteOnboardingStatus?.portfolioInsightsLockedInfo?.status
  return status === 'GRACE_PERIOD' || status === 'LOCKED'
}

export function shouldShowEliteRenewalBenefitsHeadline(
  eliteOnboardingStatus?: EliteOnboardingStatusResponse | null
): boolean {
  if (shouldShowElitePortfolioInsightsLockBanner(eliteOnboardingStatus)) {
    return false
  }
  if (eliteOnboardingStatus?.portfolioInsightsLockedInfo?.status !== 'NA') {
    return false
  }
  if (isEliteAutoPayActive(eliteOnboardingStatus) || shouldHideEliteRenewalPlansForAutoPayRetrying(eliteOnboardingStatus)) {
    return false
  }
  return isEliteSubscriptionPastExpiry(eliteOnboardingStatus) || shouldShowEliteRenewalPrompt(eliteOnboardingStatus)
}

export function getEliteRenewalHeadlineFirstLine(eliteOnboardingStatus?: EliteOnboardingStatusResponse | null): string {
  if (!shouldShowEliteRenewalBenefitsHeadline(eliteOnboardingStatus)) {
    return 'Now invest like'
  }
  if (isEliteSubscriptionPastExpiry(eliteOnboardingStatus)) {
    return 'Renew now to restore'
  }
  return 'Renew now to retain'
}
